import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Pencil, Clock, Calendar, MessageSquare, FolderOpen, ExternalLink, Paperclip, Download, Reply, FileCheck, Trash2, Upload, Link, Plus, Save, ClipboardCheck, ClipboardList, CalendarDays } from 'lucide-react';
import TSIRTab from '../components/TSIRTab';
import PunchlistTab from '../components/PunchlistTab';
import CalendarTab from '../components/CalendarTab';
import ScheduleTab from '../components/ScheduleTab';

const API_URL = '';

export default function DesignDepartment({ user, onLogout, theme, onThemeToggle }) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Check if navigated from dashboard with specific tab
  const [activeTab, setActiveTab] = useState(() => {
    const stateTab = location.state?.activeTab;
    if (stateTab === 'rfi') return 'rfi';
    if (stateTab === 'design-review') return 'design-review';
    if (stateTab === 'calendar') return 'calendar';
    return searchParams.get('tab') || 'dtr';
  });
  const [dtrEntries, setDtrEntries] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);
  const [rfis, setRfis] = useState([]);
  const [openRfis, setOpenRfis] = useState([]);
  const [projectSchedules, setProjectSchedules] = useState([]);
  const [projects, setProjects] = useState([]);
  
  // Design Review state
  const [designReviews, setDesignReviews] = useState([]);
  const [designReviewForm, setDesignReviewForm] = useState({
    project: '',
    drawing_title: '',
    google_drive_link: '',
    description: ''
  });
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({ status: 'reviewed', comment: '' });
  const [feedbackFile, setFeedbackFile] = useState(null);
  const [uploadingFeedback, setUploadingFeedback] = useState(false);
  
  // Check if user is Principal Architect (can review)
  const isPrincipal = ['admin', 'principal', 'hr'].includes(user.role);
  
  const [dtrForm, setDtrForm] = useState({ 
    project: '', 
    date: '', 
    early_in: '', 
    early_out: '', 
    time_in: '08:00', 
    time_out: '17:00', 
    break_hours: '1',
    overtime_in: '',
    overtime_out: '',
    ot_code: '',
    work_description: '' 
  });
  const [otCodeVerified, setOtCodeVerified] = useState(false);
  const [otCodeMessage, setOtCodeMessage] = useState('');
  const [verifyingOtCode, setVerifyingOtCode] = useState(false);
  const [approvedOtHours, setApprovedOtHours] = useState(0);
  const [myOtApps, setMyOtApps] = useState([]);
  const [showPrevOt, setShowPrevOt] = useState(false);
  const [reimbForm, setReimbForm] = useState({ amount: '', description: '', receipt: null, receipt_url: '', project_id: '', project_name: '', date: new Date().toISOString().split('T')[0] });
  const [rfiForm, setRfiForm] = useState({ title: '', description: '', project: '', attachment: null, submission_id: '' });
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [showRespondDialog, setShowRespondDialog] = useState(false);
  const [selectedRfi, setSelectedRfi] = useState(null);
  
  // Construction References state
  const [constructionReferences, setConstructionReferences] = useState([]);
  const [selectedRefProject, setSelectedRefProject] = useState('');
  const [currentRefData, setCurrentRefData] = useState(null);
  const [newLinkForm, setNewLinkForm] = useState({ title: '', url: '', description: '' });
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [savingRef, setSavingRef] = useState(false);

  useEffect(() => {
    loadData();
    // Check for responseId in URL params
    const responseId = searchParams.get('responseId');
    if (responseId) {
      setActiveTab('rfi');
    }
  }, [searchParams]);

  const loadData = () => {
    axios.get(`${API_URL}/api/dtr`).then(res => setDtrEntries(res.data));
    axios.get(`${API_URL}/api/ot/my`).then(res => setMyOtApps(res.data)).catch(() => {});
    axios.get(`${API_URL}/api/reimbursements`).then(res => setReimbursements(res.data));
    axios.get(`${API_URL}/api/rfi`).then(res => {
      const allRfis = res.data;
      setRfis(allRfis.filter(r => r.type === 'response'));
      setOpenRfis(allRfis.filter(r => r.type === 'submission' && r.status === 'open'));
    });
    axios.get(`${API_URL}/api/project-schedules?department=design`).then(res => setProjectSchedules(res.data));
    axios.get(`${API_URL}/api/projects`).then(res => setProjects(res.data));
    // Load design reviews
    axios.get(`${API_URL}/api/design-review`).then(res => setDesignReviews(res.data)).catch(err => console.error('Failed to load design reviews:', err));
    // Load construction references
    axios.get(`${API_URL}/api/construction-references`).then(res => setConstructionReferences(res.data)).catch(err => console.error('Failed to load construction references:', err));
  };

  // Helper function to convert time string to minutes
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Calculate early work hours (12:00 AM - 7:59 AM, min 1 hour)
  const calculateEarlyHours = () => {
    if (!dtrForm.early_in || !dtrForm.early_out) return 0;
    const earlyIn = timeToMinutes(dtrForm.early_in);
    const earlyOut = timeToMinutes(dtrForm.early_out);
    
    // Validate early work time is between 00:00 and 07:59
    if (earlyIn > 479 || earlyOut > 479) return 0; // 479 = 7:59
    
    const minutes = earlyOut - earlyIn;
    const hours = minutes / 60;
    
    // Minimum 1 hour to count as early work
    return hours >= 1 ? hours : 0;
  };

  // Calculate regular work hours (8:00 AM - 5:00 PM)
  const calculateRegularHours = () => {
    if (!dtrForm.time_in || !dtrForm.time_out) return 0;
    const timeIn = timeToMinutes(dtrForm.time_in);
    const timeOut = timeToMinutes(dtrForm.time_out);
    
    // Cap at regular work hours (8:00 - 17:00)
    const regularStart = Math.max(timeIn, 480); // 8:00 AM
    const regularEnd = Math.min(timeOut, 1020); // 5:00 PM
    
    if (regularEnd <= regularStart) return 0;
    
    const minutes = regularEnd - regularStart;
    return minutes / 60;
  };

  // Calculate overtime hours (5:01 PM - 11:59 PM, min 2 hours)
  const calculateOvertimeHours = () => {
    if (!dtrForm.overtime_in || !dtrForm.overtime_out) return 0;
    const otIn = timeToMinutes(dtrForm.overtime_in);
    const otOut = timeToMinutes(dtrForm.overtime_out);
    
    // Validate overtime is between 17:01 and 23:59
    if (otIn < 1021 || otOut < 1021) return 0; // 1021 = 17:01
    if (otOut > 1439) return 0; // 1439 = 23:59
    
    const minutes = otOut - otIn;
    const hours = minutes / 60;
    
    // Minimum 2 hours to count as overtime
    return hours >= 2 ? hours : 0;
  };

  // Calculate total work hours (Early + Regular - Break)
  const calculateTotalWorkHours = () => {
    const earlyHours = calculateEarlyHours();
    const regularHours = calculateRegularHours();
    const breakHours = parseFloat(dtrForm.break_hours) || 0;
    
    return Math.max(0, earlyHours + regularHours - breakHours);
  };

  // Verify OT code
  const verifyOtCode = async () => {
    if (!dtrForm.ot_code.trim()) {
      setOtCodeMessage('Please enter an OT code');
      setOtCodeVerified(false);
      return;
    }
    
    if (!dtrForm.date) {
      setOtCodeMessage('Please select a date first');
      setOtCodeVerified(false);
      return;
    }
    
    setVerifyingOtCode(true);
    try {
      const formData = new FormData();
      formData.append('ot_code', dtrForm.ot_code.trim());
      formData.append('date', dtrForm.date);
      
      const res = await axios.post(`${API_URL}/api/ot/verify-code`, formData);
      
      if (res.data.valid) {
        setOtCodeVerified(true);
        setApprovedOtHours(res.data.hours);
        setOtCodeMessage(`✓ Verified for ${res.data.hours} hour(s) on ${res.data.date}`);
        toast.success('OT code verified!');
      } else {
        setOtCodeVerified(false);
        setApprovedOtHours(0);
        setOtCodeMessage(res.data.message);
      }
    } catch (error) {
      setOtCodeVerified(false);
      setApprovedOtHours(0);
      setOtCodeMessage('Failed to verify OT code. Please try again.');
      toast.error('Failed to verify OT code');
    } finally {
      setVerifyingOtCode(false);
    }
  };

  const selectPrevOt = (ot) => {
    setDtrForm(prev => ({ ...prev, ot_code: ot.ot_code, date: ot.date }));
    setOtCodeVerified(false);
    setOtCodeMessage('');
    setShowPrevOt(false);
    toast.info(`Loaded ${ot.ot_code} — click Verify to apply`);
  };

  // Auto-compute overtime end based on approved hours
  const handleOvertimeStartChange = (startTime) => {
    setDtrForm(prev => {
      const newForm = { ...prev, overtime_in: startTime };
      
      // Auto-compute end time if we have approved hours
      if (startTime && approvedOtHours > 0) {
        const [hours, minutes] = startTime.split(':').map(Number);
        const startMinutes = hours * 60 + minutes;
        const endMinutes = startMinutes + (approvedOtHours * 60);
        
        // Cap at 23:59
        const cappedEndMinutes = Math.min(endMinutes, 23 * 60 + 59);
        const endHours = Math.floor(cappedEndMinutes / 60);
        const endMins = cappedEndMinutes % 60;
        
        newForm.overtime_out = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
      }
      
      return newForm;
    });
  };

  // Reset OT code verification when date changes
  const handleDateChange = (newDate) => {
    setDtrForm({ ...dtrForm, date: newDate });
    if (dtrForm.ot_code) {
      setOtCodeVerified(false);
      setApprovedOtHours(0);
      setOtCodeMessage('Date changed. Please verify OT code again.');
    }
  };

  const handleDtrSubmit = async (e) => {
    e.preventDefault();
    
    const totalWorkHours = calculateTotalWorkHours();
    const overtimeHours = calculateOvertimeHours();
    const earlyHours = calculateEarlyHours();
    
    if (totalWorkHours <= 0 && overtimeHours <= 0) {
      toast.error('Please enter valid work hours');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/dtr`, {
        project: dtrForm.project,
        date: dtrForm.date,
        hours: totalWorkHours.toFixed(2),
        time_in: dtrForm.time_in,
        time_out: dtrForm.time_out,
        early_in: dtrForm.early_in || null,
        early_out: dtrForm.early_out || null,
        early_hours: earlyHours.toFixed(2),
        break_hours: dtrForm.break_hours,
        overtime_in: dtrForm.overtime_in || null,
        overtime_out: dtrForm.overtime_out || null,
        overtime_hours: overtimeHours.toFixed(2),
        work_description: dtrForm.work_description
      }, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      toast.success('DTR entry logged!');
      setDtrForm({ 
        project: '', 
        date: '', 
        early_in: '', 
        early_out: '', 
        time_in: '08:00', 
        time_out: '17:00', 
        break_hours: '1',
        overtime_in: '',
        overtime_out: '',
        ot_code: '',
        work_description: '' 
      });
      setOtCodeVerified(false);
      setOtCodeMessage('');
      setApprovedOtHours(0);
      loadData();
    } catch (error) {
      toast.error('Failed to log DTR');
    }
  };

  const handleReimbursementSubmit = async (e) => {
    e.preventDefault();
    if (!reimbForm.receipt) {
      toast.error('Please select a receipt file first');
      return;
    }
    if (!reimbForm.project_id) {
      toast.error('Please select a project');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('amount', reimbForm.amount);
      formData.append('description', reimbForm.description);
      formData.append('receipt', reimbForm.receipt);
      formData.append('project_id', reimbForm.project_id);
      formData.append('project_name', reimbForm.project_name);
      formData.append('date', reimbForm.date);
      
      await axios.post(`${API_URL}/api/reimbursements`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Reimbursement request submitted!');
      setReimbForm({ amount: '', description: '', receipt: null, receipt_url: '', project_id: '', project_name: '', date: new Date().toISOString().split('T')[0] });
      // Reset file input
      const fileInput = document.getElementById('design-receipt');
      if (fileInput) fileInput.value = '';
      loadData();
    } catch (error) {
      toast.error('Failed to submit reimbursement');
    }
  };

  const handleReceiptSelect = (file) => {
    if (!file) return;
    setReimbForm(prev => ({ ...prev, receipt: file, receipt_url: file.name }));
  };

  const handleAttachmentUpload = async (file) => {
    if (!file) return null;
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/api/rfi/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.attachment_url;
    } catch (error) {
      toast.error('Failed to upload attachment');
      return null;
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRfiSubmit = async (e) => {
    e.preventDefault();
    try {
      let attachmentUrl = null;
      if (rfiForm.attachment) {
        attachmentUrl = await handleAttachmentUpload(rfiForm.attachment);
      }
      
      const formData = {
        title: rfiForm.title,
        description: rfiForm.description,
        project: rfiForm.project,
        rfi_type: 'response',
        attachment_url: attachmentUrl,
        submission_id: rfiForm.submission_id || null
      };
      
      await axios.post(`${API_URL}/api/rfi`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      toast.success('RFI response submitted!');
      setRfiForm({ title: '', description: '', project: '', attachment: null, submission_id: '' });
      setShowRespondDialog(false);
      setSelectedRfi(null);
      loadData();
    } catch (error) {
      toast.error('Failed to submit RFI');
    }
  };

  const handleExportPdf = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rfi/export/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `RFI_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('RFI report exported!');
    } catch (error) {
      toast.error('Failed to export RFI report');
    }
  };

  const openRespondDialog = (rfi) => {
    setSelectedRfi(rfi);
    setRfiForm({
      title: `Re: ${rfi.title}`,
      description: '',
      project: rfi.project || '',
      attachment: null,
      submission_id: rfi.id
    });
    setShowRespondDialog(true);
  };

  // ===== Design Review Handlers =====
  const handleDesignReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('project', designReviewForm.project);
      formData.append('drawing_title', designReviewForm.drawing_title);
      formData.append('google_drive_link', designReviewForm.google_drive_link);
      formData.append('description', designReviewForm.description || '');
      
      await axios.post(`${API_URL}/api/design-review`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Design review submitted successfully!');
      setDesignReviewForm({ project: '', drawing_title: '', google_drive_link: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit design review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    try {
      await axios.delete(`${API_URL}/api/design-review/${reviewId}`);
      toast.success('Review deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete review');
    }
  };

  const openFeedbackDialog = (review) => {
    setSelectedReview(review);
    setFeedbackForm({ status: review.status || 'reviewed', comment: review.comment || '' });
    setFeedbackFile(null);
    setShowFeedbackDialog(true);
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!selectedReview) return;
    
    try {
      // First update the comment/status
      const formData = new FormData();
      formData.append('status', feedbackForm.status);
      formData.append('comment', feedbackForm.comment);
      
      await axios.put(`${API_URL}/api/design-review/${selectedReview.id}/comment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Then upload feedback file if provided
      if (feedbackFile) {
        setUploadingFeedback(true);
        const fileFormData = new FormData();
        fileFormData.append('file', feedbackFile);
        await axios.post(`${API_URL}/api/design-review/${selectedReview.id}/upload-comment-file`, fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Feedback submitted successfully!');
      setShowFeedbackDialog(false);
      setSelectedReview(null);
      setFeedbackFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setUploadingFeedback(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-500 border-green-500/30';
      case 'revision_needed':
        return 'bg-orange-500/20 text-orange-500 border-orange-500/30';
      case 'reviewed':
        return 'bg-blue-500/20 text-blue-500 border-blue-500/30';
      default:
        return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'revision_needed': return 'Revision Needed';
      case 'reviewed': return 'Reviewed';
      default: return 'Pending';
    }
  };

  // Download feedback attachment
  const handleDownloadAttachment = async (fileUrl) => {
    if (!fileUrl) {
      toast.error('No attachment file available');
      return;
    }
    
    try {
      const fullUrl = `${API_URL}${fileUrl}`;
      console.log('Attempting download from:', fullUrl);
      
      // Extract filename from URL - make sure to keep the extension
      const urlParts = fileUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      
      // Ensure filename has extension
      const hasExtension = /\.[a-zA-Z0-9]+$/.test(fileName);
      const downloadFileName = hasExtension ? fileName : `${fileName}.pdf`;
      
      console.log('Download filename:', downloadFileName);
      
      // Use fetch for blob downloads
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Accept': '*/*',
        },
      });
      
      console.log('Response status:', response.status, response.statusText);
      const contentType = response.headers.get('content-type');
      console.log('Content-Type:', contentType);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      if (contentType && contentType.includes('text/html')) {
        console.error('Received HTML instead of file - URL might be wrong');
        throw new Error('File not found on server');
      }
      
      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'type:', blob.type);
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }
      
      // Create blob with correct MIME type if we can determine it
      let finalBlob = blob;
      if (blob.type === '' || blob.type === 'application/octet-stream') {
        // Try to set correct MIME type based on extension
        const ext = downloadFileName.split('.').pop().toLowerCase();
        const mimeTypes = {
          'pdf': 'application/pdf',
          'png': 'image/png',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'gif': 'image/gif'
        };
        if (mimeTypes[ext]) {
          finalBlob = new Blob([blob], { type: mimeTypes[ext] });
        }
      }
      
      const downloadUrl = window.URL.createObjectURL(finalBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = downloadFileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        if (link.parentNode) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(downloadUrl);
      }, 200);
      
      toast.success('Attachment downloaded!');
    } catch (error) {
      console.error('Download error:', error.message, error);
      toast.error(`Download failed: ${error.message}`);
      
      // Fallback: try opening in new tab
      try {
        const fullUrl = `${API_URL}${fileUrl}`;
        window.open(fullUrl, '_blank');
        toast.info('Trying to open file in new tab instead...');
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    }
  };

  // Construction References functions
  const loadProjectReferences = async (projectId) => {
    if (!projectId) {
      setCurrentRefData(null);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/construction-references/${projectId}`);
      setCurrentRefData(res.data);
    } catch (err) {
      console.error('Failed to load project references:', err);
      toast.error('Failed to load references');
    }
  };

  const handleRefProjectChange = (projectId) => {
    setSelectedRefProject(projectId);
    loadProjectReferences(projectId);
    setNewLinkForm({ title: '', url: '', description: '' });
    setEditingLinkId(null);
  };

  const handleAddLink = async () => {
    if (!selectedRefProject) {
      toast.error('Please select a project first');
      return;
    }
    if (!newLinkForm.title || !newLinkForm.url) {
      toast.error('Title and URL are required');
      return;
    }
    
    setSavingRef(true);
    try {
      await axios.post(`${API_URL}/api/construction-references/${selectedRefProject}/link`, newLinkForm);
      toast.success('Link added successfully');
      setNewLinkForm({ title: '', url: '', description: '' });
      loadProjectReferences(selectedRefProject);
      // Refresh the full list
      const res = await axios.get(`${API_URL}/api/construction-references`);
      setConstructionReferences(res.data);
    } catch (err) {
      console.error('Failed to add link:', err);
      toast.error(err.response?.data?.detail || 'Failed to add link');
    } finally {
      setSavingRef(false);
    }
  };

  const handleUpdateLink = async (linkId, linkData) => {
    setSavingRef(true);
    try {
      await axios.put(`${API_URL}/api/construction-references/${selectedRefProject}/link/${linkId}`, linkData);
      toast.success('Link updated');
      setEditingLinkId(null);
      loadProjectReferences(selectedRefProject);
      // Refresh the full list
      const res = await axios.get(`${API_URL}/api/construction-references`);
      setConstructionReferences(res.data);
    } catch (err) {
      console.error('Failed to update link:', err);
      toast.error('Failed to update link');
    } finally {
      setSavingRef(false);
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/construction-references/${selectedRefProject}/link/${linkId}`);
      toast.success('Link deleted');
      loadProjectReferences(selectedRefProject);
      // Refresh the full list
      const res = await axios.get(`${API_URL}/api/construction-references`);
      setConstructionReferences(res.data);
    } catch (err) {
      console.error('Failed to delete link:', err);
      toast.error('Failed to delete link');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} theme={theme} onThemeToggle={onThemeToggle}>
      <div className="p-4 sm:p-8 space-y-6 sm:space-y-8" data-testid="design-department-page">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/20 rounded-md flex items-center justify-center">
            <Pencil className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-foreground">Design Department</h1>
            <p className="text-xs sm:text-base text-muted-foreground">Manage design workflows, schedules, and RFI responses</p>
          </div>
        </div>

        <Tabs scope="design" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-2">
            <TabsList className="bg-muted/50 flex-wrap h-auto gap-1 justify-start w-full sm:w-auto">
              <TabsTrigger value="dtr" data-testid="tab-dtr" className="whitespace-nowrap text-xs sm:text-sm">DTR & Hours</TabsTrigger>
              <TabsTrigger value="reimbursement" data-testid="tab-reimbursement" className="whitespace-nowrap text-xs sm:text-sm">Reimburse</TabsTrigger>
              <TabsTrigger value="design-review" data-testid="tab-design-review" className="whitespace-nowrap text-xs sm:text-sm">
                <FileCheck className="w-4 h-4 mr-1" />
                Design Review
              </TabsTrigger>
              <TabsTrigger value="rfi" data-testid="tab-rfi" className="whitespace-nowrap text-xs sm:text-sm">RFI Response</TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tab-schedule" className="whitespace-nowrap text-xs sm:text-sm">Schedule</TabsTrigger>
              <TabsTrigger value="references" data-testid="tab-references" className="whitespace-nowrap text-xs sm:text-sm">
                <Link className="w-4 h-4 mr-1" />
                References
              </TabsTrigger>
              <TabsTrigger value="tsir" data-testid="tab-tsir" className="whitespace-nowrap text-xs sm:text-sm">
                <ClipboardCheck className="w-4 h-4 mr-1" />
                TSIR
              </TabsTrigger>
              <TabsTrigger value="punchlist" data-testid="tab-punchlist" className="whitespace-nowrap text-xs sm:text-sm">
                <ClipboardList className="w-4 h-4 mr-1" />
                Punchlist
              </TabsTrigger>
              <TabsTrigger value="calendar" data-testid="tab-calendar" className="whitespace-nowrap text-xs sm:text-sm">
                <CalendarDays className="w-4 h-4 mr-1" />
                Calendar
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="dtr" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="dtr-form-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary" />
                  <CardTitle>Log DTR Entry</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleDtrSubmit} className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="project">Project Name</Label>
                      <select
                        id="project"
                        data-testid="input-project"
                        className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                        value={dtrForm.project}
                        onChange={(e) => setDtrForm({ ...dtrForm, project: e.target.value })}
                        required
                      >
                        <option value="">Select Project</option>
                        {projects.map((proj) => (
                          <option key={proj.id} value={proj.project_name}>
                            {proj.project_name} ({proj.project_code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="date">Date</Label>
                      <Input
                        id="date"
                        type="date"
                        data-testid="input-date"
                        value={dtrForm.date}
                        onChange={(e) => handleDateChange(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Early Work Section */}
                  <div className="p-4 rounded-lg border border-purple-500/30 bg-purple-500/5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <Label className="text-purple-400 font-semibold">Early Work (12:00 AM - 7:59 AM)</Label>
                      <span className="text-xs text-muted-foreground ml-auto">Min. 1 hour required</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="early_in" className="text-sm">Early Time In</Label>
                        <Input
                          id="early_in"
                          type="time"
                          data-testid="input-early-in"
                          value={dtrForm.early_in}
                          onChange={(e) => setDtrForm({ ...dtrForm, early_in: e.target.value })}
                          min="00:00"
                          max="07:59"
                        />
                      </div>
                      <div>
                        <Label htmlFor="early_out" className="text-sm">Early Time Out</Label>
                        <Input
                          id="early_out"
                          type="time"
                          data-testid="input-early-out"
                          value={dtrForm.early_out}
                          onChange={(e) => setDtrForm({ ...dtrForm, early_out: e.target.value })}
                          min="00:00"
                          max="07:59"
                        />
                      </div>
                    </div>
                    {calculateEarlyHours() > 0 && (
                      <p className="text-xs text-purple-400 mt-2">Early work: {calculateEarlyHours().toFixed(1)} hours</p>
                    )}
                    {dtrForm.early_in && dtrForm.early_out && calculateEarlyHours() === 0 && (
                      <p className="text-xs text-orange-400 mt-2">Note: Less than 1 hour - will not count as early work</p>
                    )}
                  </div>

                  {/* Regular Work Section */}
                  <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                      <Label className="text-primary font-semibold">Regular Work Hours (8:00 AM - 5:00 PM)</Label>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="time_in" className="text-sm">Time In</Label>
                        <Input
                          id="time_in"
                          type="time"
                          data-testid="input-time-in"
                          value={dtrForm.time_in}
                          onChange={(e) => setDtrForm({ ...dtrForm, time_in: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="time_out" className="text-sm">Time Out</Label>
                        <Input
                          id="time_out"
                          type="time"
                          data-testid="input-time-out"
                          value={dtrForm.time_out}
                          onChange={(e) => setDtrForm({ ...dtrForm, time_out: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="break_hours" className="text-sm">Break Time (hours)</Label>
                        <Input
                          id="break_hours"
                          type="number"
                          step="0.5"
                          min="0"
                          max="3"
                          data-testid="input-break-hours"
                          value={dtrForm.break_hours}
                          onChange={(e) => setDtrForm({ ...dtrForm, break_hours: e.target.value })}
                          placeholder="1"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-primary mt-2">Regular work: {calculateRegularHours().toFixed(1)} hours (before break deduction)</p>
                  </div>

                  {/* Overtime Section */}
                  <div className="p-4 rounded-lg border border-orange-500/30 bg-orange-500/5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      <Label className="text-orange-400 font-semibold">Overtime (5:01 PM - 11:59 PM)</Label>
                      <span className="text-xs text-muted-foreground ml-auto">Min. 2 hours required</span>
                    </div>
                    
                    {/* OT Code Verification */}
                    <div className="mb-4 p-3 bg-background/50 rounded-lg border border-border/40">
                      <Label className="text-sm mb-2 block">OT Code</Label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Enter OT Code (e.g., OT-ABC123)"
                          data-testid="input-ot-code"
                          value={dtrForm.ot_code}
                          onChange={(e) => {
                            setDtrForm({ ...dtrForm, ot_code: e.target.value.toUpperCase() });
                            setOtCodeVerified(false);
                            setOtCodeMessage('');
                          }}
                          className="flex-1"
                        />
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={verifyOtCode}
                          disabled={verifyingOtCode || !dtrForm.ot_code.trim()}
                          data-testid="btn-verify-ot-code"
                        >
                          {verifyingOtCode ? 'Verifying...' : 'Verify'}
                        </Button>
                      </div>
                      {otCodeMessage && (
                        <p className={`text-xs mt-2 ${otCodeVerified ? 'text-green-500' : 'text-amber-500'}`}>
                          {otCodeMessage}
                        </p>
                      )}
                      {!otCodeVerified && (
                        <p className="text-xs text-muted-foreground mt-2">
                          ⚠️ OT application must be approved to get a code. Contact Admin if not yet approved.
                        </p>
                      )}
                      <div className="mt-3 border-t border-border/40 pt-2">
                        <button type="button" onClick={() => setShowPrevOt(v => !v)}
                          className="text-xs text-primary hover:underline" data-testid="btn-toggle-prev-ot">
                          {showPrevOt ? 'Hide previous OT applications' : 'Show previous OT applications'}
                        </button>
                        {showPrevOt && (
                          <div className="mt-2 max-h-48 overflow-y-auto space-y-1" data-testid="prev-ot-list">
                            {myOtApps.length === 0 && <p className="text-xs text-muted-foreground">No OT applications yet.</p>}
                            {myOtApps.map(ot => {
                              const usable = ot.status === 'approved' && ot.ot_code;
                              return (
                                <button key={ot.id} type="button"
                                  onClick={() => usable && selectPrevOt(ot)}
                                  disabled={!usable}
                                  className={`w-full text-left text-xs p-2 rounded border flex items-center justify-between gap-2 ${usable ? 'hover:bg-muted/50 border-border/40' : 'opacity-60 border-border/20 cursor-not-allowed'}`}
                                  data-testid={`prev-ot-${ot.id}`}>
                                  <span>{ot.date} · {ot.hours}h · <span className="capitalize">{ot.status}</span></span>
                                  {usable
                                    ? <span className="font-mono font-bold text-green-500">{ot.ot_code}</span>
                                    : <span className="text-muted-foreground">no code</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="overtime_in" className="text-sm">Overtime Start</Label>
                        <Input
                          id="overtime_in"
                          type="time"
                          data-testid="input-overtime-in"
                          value={dtrForm.overtime_in}
                          onChange={(e) => handleOvertimeStartChange(e.target.value)}
                          min="17:01"
                          max="23:59"
                          disabled={!otCodeVerified}
                          className={!otCodeVerified ? 'opacity-50 cursor-not-allowed' : ''}
                        />
                      </div>
                      <div>
                        <Label htmlFor="overtime_out" className="text-sm">Overtime End {approvedOtHours > 0 && <span className="text-muted-foreground">(auto-computed)</span>}</Label>
                        <Input
                          id="overtime_out"
                          type="time"
                          data-testid="input-overtime-out"
                          value={dtrForm.overtime_out}
                          onChange={(e) => setDtrForm({ ...dtrForm, overtime_out: e.target.value })}
                          min="17:01"
                          max="23:59"
                          disabled={!otCodeVerified}
                          className={!otCodeVerified ? 'opacity-50 cursor-not-allowed' : ''}
                        />
                      </div>
                    </div>
                    {calculateOvertimeHours() > 0 && (
                      <p className="text-xs text-orange-400 mt-2">Overtime: {calculateOvertimeHours().toFixed(1)} hours</p>
                    )}
                    {dtrForm.overtime_in && dtrForm.overtime_out && calculateOvertimeHours() === 0 && (
                      <p className="text-xs text-red-400 mt-2">Note: Less than 2 hours - will not count as overtime</p>
                    )}
                  </div>

                  {/* Hours Summary */}
                  <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <Label className="text-green-400 font-semibold">Hours Summary</Label>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                        <p className="text-2xl font-bold text-purple-400">{calculateEarlyHours().toFixed(1)}</p>
                        <p className="text-xs text-muted-foreground">Early Work</p>
                      </div>
                      <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                        <p className="text-2xl font-bold text-primary">{calculateRegularHours().toFixed(1)}</p>
                        <p className="text-xs text-muted-foreground">Regular</p>
                      </div>
                      <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                        <p className="text-2xl font-bold text-red-400">-{parseFloat(dtrForm.break_hours || 0).toFixed(1)}</p>
                        <p className="text-xs text-muted-foreground">Break</p>
                      </div>
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <p className="text-2xl font-bold text-green-400">{calculateTotalWorkHours().toFixed(1)}</p>
                        <p className="text-xs text-muted-foreground">Total Work</p>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20 text-center">
                      <p className="text-2xl font-bold text-orange-400">{calculateOvertimeHours().toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">Overtime Hours (Separate)</p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="work_description">Work Description</Label>
                    <Textarea
                      id="work_description"
                      data-testid="input-work-description"
                      value={dtrForm.work_description}
                      onChange={(e) => setDtrForm({ ...dtrForm, work_description: e.target.value })}
                      placeholder="Describe the work completed today..."
                      rows={3}
                      required
                    />
                  </div>
                  <Button type="submit" data-testid="btn-submit-dtr" className="w-full md:w-auto">
                    Log DTR Entry
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="glass-effect border-border/40" data-testid="dtr-entries-card">
              <CardHeader>
                <CardTitle>Your DTR Entries</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dtrEntries.map((entry) => (
                    <div key={entry.id} className="p-3 bg-muted/30 rounded-md" data-testid={`dtr-entry-${entry.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-foreground">{entry.project}</p>
                        <p className="text-sm font-mono text-primary">{entry.hours}h</p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-1">
                        <span>{entry.date}</span>
                        {entry.time_in && entry.time_out && (
                          <span className="font-mono">{entry.time_in} - {entry.time_out}</span>
                        )}
                      </div>
                      {entry.work_description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{entry.work_description}</p>
                      )}
                    </div>
                  ))}
                  {dtrEntries.length === 0 && <p className="text-muted-foreground text-sm">No entries yet</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reimbursement" className="space-y-6">
            {/* Gas Expense Calculator */}
            <Card className="glass-effect border-border/40" data-testid="gas-calculator-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <CardTitle>Gas Expense Calculator</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Calculate reimbursement for using your own vehicle</p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="gas-distance">Distance (km)</Label>
                    <Input
                      id="gas-distance"
                      type="number"
                      step="0.1"
                      min="0"
                      data-testid="input-gas-distance"
                      placeholder="e.g., 25"
                      className="bg-background/50"
                      onChange={(e) => {
                        const distanceInput = document.getElementById('gas-distance');
                        const priceInput = document.getElementById('gas-price');
                        const resultDiv = document.getElementById('gas-result');
                        const distance = parseFloat(distanceInput.value) || 0;
                        const price = parseFloat(priceInput.value) || 0;
                        const total = (distance / 10) * price;
                        resultDiv.textContent = total > 0 ? `₱${total.toFixed(2)}` : '₱0.00';
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="gas-price">Current Gas Price (₱/L)</Label>
                    <Input
                      id="gas-price"
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="input-gas-price"
                      placeholder="e.g., 65.50"
                      className="bg-background/50"
                      onChange={(e) => {
                        const distanceInput = document.getElementById('gas-distance');
                        const priceInput = document.getElementById('gas-price');
                        const resultDiv = document.getElementById('gas-result');
                        const distance = parseFloat(distanceInput.value) || 0;
                        const price = parseFloat(priceInput.value) || 0;
                        const total = (distance / 10) * price;
                        resultDiv.textContent = total > 0 ? `₱${total.toFixed(2)}` : '₱0.00';
                      }}
                    />
                  </div>
                  <div>
                    <Label>Computed Amount</Label>
                    <div className="h-11 flex items-center px-3 rounded-md border border-input bg-primary/10">
                      <span id="gas-result" className="text-lg font-bold text-primary" data-testid="gas-result">₱0.00</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Formula: (Distance ÷ 10) × Gas Price — Use this computed amount for your reimbursement request below.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-effect border-border/40" data-testid="reimbursement-form-card">
              <CardHeader>
                <CardTitle>Submit Reimbursement Request</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleReimbursementSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="amount">Amount (PHP)</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        data-testid="input-amount"
                        value={reimbForm.amount}
                        onChange={(e) => setReimbForm({ ...reimbForm, amount: e.target.value })}
                        placeholder="500.00"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="design-receipt">Receipt Compilation (Required)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="design-receipt"
                          type="file"
                          accept="image/*,.pdf"
                          data-testid="input-receipt"
                          onChange={(e) => handleReceiptSelect(e.target.files[0])}
                          className="flex-1"
                        />
                      </div>
                      {reimbForm.receipt && (
                        <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                          <Paperclip className="w-3 h-3" />
                          {reimbForm.receipt.name} selected
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="reimb-project">Project (Required)</Label>
                      <select
                        id="reimb-project"
                        data-testid="select-reimb-project"
                        value={reimbForm.project_id}
                        onChange={(e) => {
                          const p = projects.find(pr => pr.id === e.target.value);
                          setReimbForm({ ...reimbForm, project_id: e.target.value, project_name: p ? p.project_name : '' });
                        }}
                        required
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="">Select project...</option>
                        {projects.map(p => (
                          <option key={p.id} value={p.id}>{p.project_name} ({p.project_code})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="reimb-date">Date</Label>
                      <Input
                        id="reimb-date"
                        type="date"
                        data-testid="input-reimb-date"
                        value={reimbForm.date}
                        onChange={(e) => setReimbForm({ ...reimbForm, date: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      data-testid="input-reimb-description"
                      value={reimbForm.description}
                      onChange={(e) => setReimbForm({ ...reimbForm, description: e.target.value })}
                      placeholder="Transportation, printing, materials..."
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={!reimbForm.receipt}
                    data-testid="btn-submit-reimbursement"
                  >
                    Submit Request
                  </Button>
                  {!reimbForm.receipt && (
                    <p className="text-xs text-muted-foreground">Select receipt file to enable submission</p>
                  )}
                </form>
              </CardContent>
            </Card>

            <Card className="glass-effect border-border/40" data-testid="reimbursement-list-card">
              <CardHeader>
                <CardTitle>Your Reimbursement Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {reimbursements.map((req) => (
                    <div key={req.id} className="p-3 bg-muted/30 rounded-md" data-testid={`reimb-${req.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-foreground">{req.description}</p>
                        <p className="text-sm font-mono text-primary">₱{req.amount}</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs flex-wrap">
                        <span className={`px-2 py-0.5 rounded capitalize font-medium ${
                          req.status === 'paid' ? 'bg-emerald-500/25 text-emerald-600' :
                          req.status === 'for_payment' ? 'bg-blue-500/25 text-blue-600' :
                          req.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                          req.status === 'rejected' || req.status === 'declined' ? 'bg-red-500/20 text-red-500' :
                          'bg-primary/20 text-primary'
                        }`}>
                          {req.status === 'for_payment' ? 'For Payment' : req.status}
                        </span>
                        {req.receipt_url && (
                          <a 
                            href={`${API_URL}${req.receipt_url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            <Paperclip className="w-3 h-3" />
                            Receipt
                          </a>
                        )}
                        {(req.status === 'for_payment' || req.status === 'paid') && (
                          <button
                            onClick={async () => {
                              try {
                                const response = await axios.get(`${API_URL}/api/reimbursement/${req.id}/export`, { responseType: 'blob' });
                                const blob = new Blob([response.data], { type: 'application/pdf' });
                                const u = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = u;
                                link.download = `Reimbursement_${req.id.slice(0,8)}_${new Date().toISOString().split('T')[0]}.pdf`;
                                document.body.appendChild(link); link.click(); link.remove();
                                window.URL.revokeObjectURL(u);
                                toast.success('PDF downloaded');
                              } catch { toast.error('Download failed'); }
                            }}
                            className="text-primary hover:underline flex items-center gap-1 font-medium"
                            data-testid={`btn-download-reimb-pdf-${req.id}`}
                          >
                            <Download className="w-3 h-3" />
                            Download PDF
                          </button>
                        )}
                        <span className="text-muted-foreground">
                          {new Date(req.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                  {reimbursements.length === 0 && <p className="text-muted-foreground text-sm">No requests yet</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Design Review Tab */}
          <TabsContent value="design-review" className="space-y-6">
            {/* Submission Form - For Design Employees */}
            {!isPrincipal && (
              <Card className="glass-effect border-border/40" data-testid="design-review-form-card">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <FileCheck className="w-5 h-5 text-primary" />
                    <CardTitle>Submit Drawing for Review</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleDesignReviewSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="review-project">Project</Label>
                        <select
                          id="review-project"
                          data-testid="input-review-project"
                          className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                          value={designReviewForm.project}
                          onChange={(e) => setDesignReviewForm({ ...designReviewForm, project: e.target.value })}
                          required
                        >
                          <option value="">Select Project</option>
                          {projects.map((proj) => (
                            <option key={proj.id} value={proj.project_name}>
                              {proj.project_name} ({proj.project_code})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="drawing-title">Drawing Title</Label>
                        <Input
                          id="drawing-title"
                          data-testid="input-drawing-title"
                          value={designReviewForm.drawing_title}
                          onChange={(e) => setDesignReviewForm({ ...designReviewForm, drawing_title: e.target.value })}
                          placeholder="e.g., Floor Plan - Level 2"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="drive-link">Google Drive Link</Label>
                      <Input
                        id="drive-link"
                        type="url"
                        data-testid="input-drive-link"
                        value={designReviewForm.google_drive_link}
                        onChange={(e) => setDesignReviewForm({ ...designReviewForm, google_drive_link: e.target.value })}
                        placeholder="https://drive.google.com/..."
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">Share your Google Drive link containing the drawing files</p>
                    </div>
                    <div>
                      <Label htmlFor="review-description">Description (Optional)</Label>
                      <Textarea
                        id="review-description"
                        data-testid="input-review-description"
                        value={designReviewForm.description}
                        onChange={(e) => setDesignReviewForm({ ...designReviewForm, description: e.target.value })}
                        placeholder="Additional notes or context for the reviewer..."
                        rows={3}
                      />
                    </div>
                    <Button type="submit" data-testid="btn-submit-design-review">
                      <Upload className="w-4 h-4 mr-2" />
                      Submit for Review
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Reviews List */}
            <Card className="glass-effect border-border/40" data-testid="design-reviews-list-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <FileCheck className="w-5 h-5 text-primary" />
                  <CardTitle>
                    {isPrincipal ? 'All Design Review Submissions' : 'My Design Review Submissions'}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {designReviews.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border/40">
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Drawing</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Project</th>
                          {isPrincipal && (
                            <th className="text-left py-3 px-2 font-medium text-muted-foreground">Submitted By</th>
                          )}
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Status</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Date</th>
                          <th className="text-right py-3 px-2 font-medium text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {designReviews.map((review) => (
                          <tr key={review.id} className="border-b border-border/20 hover:bg-muted/20" data-testid={`review-row-${review.id}`}>
                            <td className="py-3 px-2">
                              <div>
                                <p className="font-medium text-foreground">{review.drawing_title}</p>
                                {review.description && (
                                  <p className="text-xs text-muted-foreground line-clamp-1">{review.description}</p>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-muted-foreground">{review.project}</td>
                            {isPrincipal && (
                              <td className="py-3 px-2 text-muted-foreground">{review.submitted_by_name}</td>
                            )}
                            <td className="py-3 px-2">
                              <span className={`px-2 py-1 rounded text-xs border ${getStatusBadge(review.status)}`}>
                                {getStatusText(review.status)}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-muted-foreground text-xs">
                              {new Date(review.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center justify-end gap-2">
                                <a
                                  href={review.google_drive_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 hover:bg-primary/20 rounded text-primary"
                                  title="Open Drawing"
                                  data-testid={`btn-open-link-${review.id}`}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                                {review.comment_file_url && (
                                  <button
                                    onClick={() => handleDownloadAttachment(review.comment_file_url)}
                                    className="p-2 hover:bg-blue-500/20 rounded text-blue-500"
                                    title="Download Feedback Attachment"
                                    data-testid={`btn-download-feedback-${review.id}`}
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                )}
                                {isPrincipal && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openFeedbackDialog(review)}
                                    data-testid={`btn-add-feedback-${review.id}`}
                                  >
                                    <MessageSquare className="w-4 h-4 mr-1" />
                                    Feedback
                                  </Button>
                                )}
                                {(review.submitted_by_id === user.id || isPrincipal) && (
                                  <button
                                    onClick={() => handleDeleteReview(review.id)}
                                    className="p-2 hover:bg-red-500/20 rounded text-red-500"
                                    title="Delete"
                                    data-testid={`btn-delete-review-${review.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No design review submissions yet</p>
                    {!isPrincipal && <p className="text-sm">Submit a drawing above to get started</p>}
                  </div>
                )}
                
                {/* Review feedback display */}
                {designReviews.filter(r => r.comment).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-border/40">
                    <h3 className="text-sm font-medium text-foreground mb-3">Recent Feedback</h3>
                    <div className="space-y-3">
                      {designReviews.filter(r => r.comment).slice(0, 5).map((review) => (
                        <div key={review.id} className="p-3 bg-muted/30 rounded-md">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-foreground text-sm">{review.drawing_title}</span>
                            <span className={`px-2 py-0.5 rounded text-xs border ${getStatusBadge(review.status)}`}>
                              {getStatusText(review.status)}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">{review.comment}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            By {review.reviewed_by_name} on {new Date(review.reviewed_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rfi" className="space-y-6">
            {/* Open RFIs from Construction - Need Response */}
            {openRfis.length > 0 && (
              <Card className="glass-effect border-orange-500/40 border-2" data-testid="open-rfis-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-orange-500" />
                      <CardTitle className="text-orange-500">Pending RFIs - Need Response ({openRfis.length})</CardTitle>
                    </div>
                    <Button variant="outline" onClick={handleExportPdf} data-testid="btn-export-rfi-pdf">
                      <Download className="w-4 h-4 mr-2" />
                      Export PDF
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {openRfis.map((rfi) => (
                      <div key={rfi.id} className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-md" data-testid={`open-rfi-${rfi.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-foreground">{rfi.title}</p>
                            {rfi.project && (
                              <p className="text-xs text-muted-foreground">Project: {rfi.project}</p>
                            )}
                          </div>
                          <span className="text-xs px-2 py-1 bg-orange-500/20 text-orange-500 rounded">
                            Awaiting Response
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{rfi.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span>From: {rfi.submitted_by_name || 'Construction'}</span>
                            {rfi.attachment_url && (
                              <a 
                                href={`${API_URL}${rfi.attachment_url}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-primary hover:underline"
                              >
                                <Paperclip className="w-3 h-3" />
                                Attachment
                              </a>
                            )}
                            <span>{new Date(rfi.created_at).toLocaleDateString()}</span>
                          </div>
                          <Button 
                            size="sm" 
                            onClick={() => openRespondDialog(rfi)}
                            data-testid={`btn-respond-${rfi.id}`}
                          >
                            <Reply className="w-4 h-4 mr-1" />
                            Respond
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="glass-effect border-border/40" data-testid="rfi-list-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    <CardTitle>Your RFI Responses</CardTitle>
                  </div>
                  {openRfis.length === 0 && (
                    <Button variant="outline" onClick={handleExportPdf} data-testid="btn-export-rfi-pdf-alt">
                      <Download className="w-4 h-4 mr-2" />
                      Export PDF
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {rfis.map((rfi) => (
                    <div key={rfi.id} className="p-4 bg-muted/30 rounded-md" data-testid={`rfi-${rfi.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-foreground">{rfi.title}</p>
                          {rfi.project && (
                            <p className="text-xs text-muted-foreground">Project: {rfi.project}</p>
                          )}
                        </div>
                        <span className="text-xs px-2 py-1 bg-green-500/20 text-green-500 rounded">
                          Responded
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{rfi.description}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {rfi.attachment_url && (
                          <a 
                            href={`${API_URL}${rfi.attachment_url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <Paperclip className="w-3 h-3" />
                            Attachment
                          </a>
                        )}
                        <span>{new Date(rfi.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                  {rfis.length === 0 && <p className="text-muted-foreground text-sm">No RFI responses yet</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule">
            <ScheduleTab department="Design" user={user} />
          </TabsContent>

          {/* References Tab - Manage construction drawing links */}
          <TabsContent value="references" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="references-management-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Link className="w-5 h-5 text-blue-500" />
                  <CardTitle>Manage Construction Drawing References</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Add and manage links to the latest construction drawings for each project. These links will be visible to the Construction team.
                </p>

                {/* Project Selector */}
                <div className="flex items-center gap-4 flex-wrap">
                  <Label className="text-sm font-medium whitespace-nowrap">Select Project:</Label>
                  <select
                    value={selectedRefProject}
                    onChange={(e) => handleRefProjectChange(e.target.value)}
                    className="h-10 rounded-md border border-input bg-background/50 px-3 text-sm min-w-[250px]"
                    data-testid="ref-project-selector"
                  >
                    <option value="">Choose a project to manage</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.project_name} ({p.project_code})</option>
                    ))}
                  </select>
                </div>

                {selectedRefProject && currentRefData && (
                  <div className="space-y-4">
                    {/* Add New Link Form */}
                    <div className="p-4 border border-dashed border-border/60 rounded-lg bg-muted/20">
                      <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Add New Drawing Link
                      </h4>
                      <div className="grid md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Title *</Label>
                          <Input
                            value={newLinkForm.title}
                            onChange={(e) => setNewLinkForm({ ...newLinkForm, title: e.target.value })}
                            placeholder="e.g., Architectural Plans"
                            data-testid="input-new-link-title"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">URL *</Label>
                          <Input
                            value={newLinkForm.url}
                            onChange={(e) => setNewLinkForm({ ...newLinkForm, url: e.target.value })}
                            placeholder="https://drive.google.com/..."
                            data-testid="input-new-link-url"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Description (optional)</Label>
                          <Input
                            value={newLinkForm.description}
                            onChange={(e) => setNewLinkForm({ ...newLinkForm, description: e.target.value })}
                            placeholder="Brief description"
                            data-testid="input-new-link-desc"
                          />
                        </div>
                      </div>
                      <Button 
                        className="mt-3" 
                        onClick={handleAddLink} 
                        disabled={savingRef || !newLinkForm.title || !newLinkForm.url}
                        data-testid="btn-add-link"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {savingRef ? 'Adding...' : 'Add Link'}
                      </Button>
                    </div>

                    {/* Existing Links */}
                    <div>
                      <h4 className="font-medium text-foreground mb-3">Current Links ({currentRefData.drawing_links?.length || 0})</h4>
                      {currentRefData.drawing_links && currentRefData.drawing_links.length > 0 ? (
                        <div className="space-y-2">
                          {currentRefData.drawing_links.map(link => (
                            <div
                              key={link.id}
                              className="flex items-center justify-between p-3 bg-muted/30 rounded-md border border-border/40"
                            >
                              {editingLinkId === link.id ? (
                                <div className="flex-1 grid md:grid-cols-3 gap-2 mr-3">
                                  <Input
                                    defaultValue={link.title}
                                    id={`edit-title-${link.id}`}
                                    placeholder="Title"
                                  />
                                  <Input
                                    defaultValue={link.url}
                                    id={`edit-url-${link.id}`}
                                    placeholder="URL"
                                  />
                                  <Input
                                    defaultValue={link.description || ''}
                                    id={`edit-desc-${link.id}`}
                                    placeholder="Description"
                                  />
                                </div>
                              ) : (
                                <div className="flex items-center gap-3 flex-1">
                                  <ExternalLink className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <a 
                                      href={link.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="font-medium text-foreground hover:text-primary transition-colors"
                                    >
                                      {link.title}
                                    </a>
                                    {link.description && (
                                      <p className="text-xs text-muted-foreground truncate">{link.description}</p>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {editingLinkId === link.id ? (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        const title = document.getElementById(`edit-title-${link.id}`)?.value;
                                        const url = document.getElementById(`edit-url-${link.id}`)?.value;
                                        const description = document.getElementById(`edit-desc-${link.id}`)?.value;
                                        handleUpdateLink(link.id, { title, url, description });
                                      }}
                                      disabled={savingRef}
                                    >
                                      <Save className="w-4 h-4 text-green-500" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setEditingLinkId(null)}
                                    >
                                      <span className="text-xs">Cancel</span>
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setEditingLinkId(link.id)}
                                      data-testid={`btn-edit-link-${link.id}`}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteLink(link.id)}
                                      data-testid={`btn-delete-link-${link.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic py-4 text-center">
                          No drawing links added yet. Use the form above to add one.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {!selectedRefProject && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Link className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Select a project above to manage its drawing references</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TSIR Tab */}
          <TabsContent value="tsir" className="space-y-6">
            <TSIRTab user={user} projects={projects} />
          </TabsContent>

          {/* Punchlist Tab */}
          <TabsContent value="punchlist" className="space-y-6">
            <PunchlistTab user={user} projects={projects} isReadOnly={false} />
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-6">
            <CalendarTab user={user} projects={projects} department="Design" />
          </TabsContent>
        </Tabs>

        {/* Respond Dialog */}
        <Dialog open={showRespondDialog} onOpenChange={setShowRespondDialog}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <DialogTitle>Respond to RFI</DialogTitle>
            </DialogHeader>
            {selectedRfi && (
              <div className="space-y-4">
                <div className="p-3 bg-muted/30 rounded-md">
                  <p className="text-sm font-medium text-foreground mb-1">Original RFI:</p>
                  <p className="text-sm text-muted-foreground">{selectedRfi.title}</p>
                  <p className="text-xs text-muted-foreground mt-2">{selectedRfi.description}</p>
                </div>
                <form onSubmit={handleRfiSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="response_title">Response Title</Label>
                      <Input
                        id="response_title"
                        data-testid="input-response-title"
                        value={rfiForm.title}
                        onChange={(e) => setRfiForm({ ...rfiForm, title: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="response_project">Project</Label>
                      <select
                        id="response_project"
                        data-testid="input-response-project"
                        className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                        value={rfiForm.project}
                        onChange={(e) => setRfiForm({ ...rfiForm, project: e.target.value })}
                      >
                        <option value="">Select Project</option>
                        {projects.map((proj) => (
                          <option key={proj.id} value={proj.project_name}>
                            {proj.project_name} ({proj.project_code})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="response_description">Response / Clarification</Label>
                    <Textarea
                      id="response_description"
                      rows={5}
                      data-testid="input-response-description"
                      value={rfiForm.description}
                      onChange={(e) => setRfiForm({ ...rfiForm, description: e.target.value })}
                      placeholder="Design clarification, updated drawings, impact assessment..."
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="response_attachment">Attachment (Optional)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="response_attachment"
                        type="file"
                        data-testid="input-response-attachment"
                        onChange={(e) => setRfiForm({ ...rfiForm, attachment: e.target.files[0] })}
                        className="flex-1"
                      />
                      {rfiForm.attachment && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Paperclip className="w-3 h-3" />
                          {rfiForm.attachment.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowRespondDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={uploadingAttachment} data-testid="btn-submit-response">
                      {uploadingAttachment ? 'Uploading...' : 'Submit Response'}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Design Review Feedback Dialog */}
        <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Review Feedback</DialogTitle>
            </DialogHeader>
            {selectedReview && (
              <div className="space-y-4">
                <div className="p-3 bg-muted/30 rounded-md">
                  <p className="text-sm font-medium text-foreground">{selectedReview.drawing_title}</p>
                  <p className="text-xs text-muted-foreground">Project: {selectedReview.project}</p>
                  <p className="text-xs text-muted-foreground">Submitted by: {selectedReview.submitted_by_name}</p>
                  <a
                    href={selectedReview.google_drive_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1 mt-2"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Open Drawing in Google Drive
                  </a>
                </div>
                <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="feedback-status">Status</Label>
                    <select
                      id="feedback-status"
                      data-testid="select-feedback-status"
                      className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                      value={feedbackForm.status}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, status: e.target.value })}
                    >
                      <option value="pending">Pending</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="approved">Approved</option>
                      <option value="revision_needed">Revision Needed</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="feedback-comment">Comment / Feedback</Label>
                    <Textarea
                      id="feedback-comment"
                      data-testid="input-feedback-comment"
                      value={feedbackForm.comment}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, comment: e.target.value })}
                      placeholder="Add your feedback or revision notes..."
                      rows={4}
                    />
                  </div>
                  <div>
                    <Label htmlFor="feedback-file">Attach Feedback File (Image/PDF)</Label>
                    <Input
                      id="feedback-file"
                      type="file"
                      accept="image/*,.pdf"
                      data-testid="input-feedback-file"
                      onChange={(e) => setFeedbackFile(e.target.files[0])}
                    />
                    {feedbackFile && (
                      <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                        <Paperclip className="w-3 h-3" />
                        {feedbackFile.name}
                      </p>
                    )}
                    {selectedReview.comment_file_url && !feedbackFile && (
                      <button
                        type="button"
                        onClick={() => handleDownloadAttachment(selectedReview.comment_file_url)}
                        className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                      >
                        <Download className="w-3 h-3" />
                        Download existing attachment
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowFeedbackDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={uploadingFeedback} data-testid="btn-submit-feedback">
                      {uploadingFeedback ? 'Uploading...' : 'Submit Feedback'}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}